import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import {
  DeleteOutlineOutlined,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';

import FHConfirmDialog from '../../../../../../../components/base/dialogs/FHConfirmDialog';
import { FOutlinedValue } from '../../../../../../../components/baseV2';
import { FORTA_BLUE } from '../../../../../../../utils/colors';
import { DURATION_TIME_UNITS } from '../constants';
import {
  DURATION_EVENT_DELETE_BUTTON_SX,
  DURATION_EVENT_EDITING_FIELD_SX,
  DURATION_EVENT_FIELD_SX,
  DURATION_EVENT_FIELD_TEXT_SX,
  DURATION_EVENT_ICON_BUTTON_SX,
  DURATION_EVENT_STEPPER_ROW_SX,
  DURATION_EVENT_UNIT_SX,
  DURATION_EVENT_VALUE_SX,
} from '../styles';
import { formatDurationTime, getBoundedDuration, getDurationTimeParts } from '../utils';

import type { MouseEventHandler } from 'react';
import type { DurationEventFieldProps } from '../types';

function DurationEventField({
  label,
  duration,
  durationLimitInSeconds,
  disabled = false,
  editable = false,
  required = false,
  onDelete,
  onUpdate,
}: DurationEventFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const durationParts = getDurationTimeParts(duration);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        event.target instanceof Node &&
        !containerRef.current.contains(event.target)
      ) {
        setIsEditing(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFieldClick = () => {
    if (!disabled && editable) {
      setIsEditing(true);
    }
  };

  const handleDeleteClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteDialog(false);
    setIsEditing(false);
    onDelete?.();
  };

  const handleDurationChange =
    (delta: number): MouseEventHandler<HTMLButtonElement> =>
    (event) => {
      event.stopPropagation();

      const nextDuration = getBoundedDuration(duration, delta, durationLimitInSeconds);

      if (nextDuration !== duration) {
        onUpdate?.(nextDuration);
      }
    };

  if (!isEditing) {
    return (
      <Box ref={containerRef} onClick={handleFieldClick}>
        <FOutlinedValue
          label={label}
          disabled={disabled}
          sx={DURATION_EVENT_FIELD_SX}
        >
          <Typography sx={DURATION_EVENT_FIELD_TEXT_SX}>
            {formatDurationTime(duration)}
          </Typography>
        </FOutlinedValue>
      </Box>
    );
  }

  return (
    <Box ref={containerRef}>
      <FOutlinedValue
        label={label}
        required={required}
        disabled={disabled}
        sx={DURATION_EVENT_EDITING_FIELD_SX}
      >
        <Box sx={DURATION_EVENT_STEPPER_ROW_SX}>
          {DURATION_TIME_UNITS.map((unit) => (
            <Box key={unit.key} sx={DURATION_EVENT_UNIT_SX}>
              <IconButton
                aria-label={`Increase ${label} ${unit.label}`}
                sx={DURATION_EVENT_ICON_BUTTON_SX}
                disabled={disabled || unit.step > durationLimitInSeconds}
                onClick={handleDurationChange(unit.step)}
              >
                <KeyboardArrowUp />
              </IconButton>
              <Typography sx={DURATION_EVENT_VALUE_SX}>
                {durationParts[unit.key]} {unit.label}
              </Typography>
              <IconButton
                aria-label={`Decrease ${label} ${unit.label}`}
                sx={DURATION_EVENT_ICON_BUTTON_SX}
                disabled={disabled || duration - unit.step < 1}
                onClick={handleDurationChange(-unit.step)}
              >
                <KeyboardArrowDown />
              </IconButton>
            </Box>
          ))}
          {onDelete != null ? (
            <IconButton
              aria-label={`Delete ${label}`}
              sx={DURATION_EVENT_DELETE_BUTTON_SX}
              disabled={disabled}
              onClick={handleDeleteClick}
            >
              <DeleteOutlineOutlined />
            </IconButton>
          ) : null}
        </Box>
      </FOutlinedValue>
      <FHConfirmDialog
        open={showDeleteDialog}
        title="Delete time entry"
        titleStyles={{
          color: FORTA_BLUE.primary,
          fontWeight: 500,
        }}
        contentRenderer={() => (
          <Box display="flex" flexDirection="column" justifyContent="center" px={1} py={6}>
            <Typography textAlign="center" fontSize={20} fontWeight={500}>
              Are you sure you want to delete this time duration entry?
            </Typography>
            <Typography textAlign="center" fontSize={21} fontWeight={500} mt={1} mb={1}>
              This action cannot be undone.
            </Typography>
          </Box>
        )}
        submittext="DELETE"
        canceltext="CANCEL"
        onClose={() => setShowDeleteDialog(false)}
        onSubmit={handleDeleteConfirm}
      />
    </Box>
  );
}

export default DurationEventField;
